import {useEffect, useMemo, useState} from "react";
import {BlobReader, BlobWriter, ZipReader} from "@zip.js/zip.js";
import Spinner from "../../../../../Spinner";
import {extname} from "../../../../../../util/path";
import PreviewTextFile from "../PreviewTextFile/PreviewTextFile";
import PostalMime from "postal-mime";
import {RTFJS} from "rtf.js";
import {stringToArrayBuffer} from "../../../../../../util/functions";
import {toDisplayableImage} from "../../../../../../util/heic";
import DownloadFile from "../DownloadFile";

export default function Entry({entry, parent}) {
	const [loading, setLoading] = useState(false);
	const [blob, setBlob] = useState(null);
	const [srcDoc, setSrcDoc] = useState(null);
	const [src, setSrc] = useState(null);
	const [entries, setEntries] = useState([]);

	async function onClick() {
		if (!blob) {
			setLoading(true);
			const blobData = await entry.getData(new BlobWriter());
			setBlob(blobData)
			setLoading(false);
		}
	}

	useEffect(() => {
		(async () => {
			setSrc(null);
			setSrcDoc(null);
			setEntries([]);
			if (blob) {
				// extname keeps whatever casing the archive stored, so normalise before matching (DEV-3002)
			const extension = extname(entry.filename).toLowerCase();

				switch (extension) {
					case ".eml":
						const mail = await PostalMime.parse(blob);
						setSrcDoc(mail.html);
						break;
					case ".rtf":
						const rtfDoc = new RTFJS.Document(stringToArrayBuffer(blob));
						const html = await rtfDoc.render();

						const doc = document.createElement("html");
						const body = document.createElement("body");
						for (const element of html) {
							body.append(element);
						}
						doc.append(body);
						setSrcDoc(doc.outerHTML);
						break;
					case ".heic":
					case ".heif":
						setSrc(URL.createObjectURL(await toDisplayableImage(blob)));
						break;
					case ".html":
					case ".csv":
					case ".json":
					case ".tsv":
					case ".txt":
						setSrcDoc(await blob.text());
						break;
					case ".pdf":
						setSrc(URL.createObjectURL(new Blob([blob], {type: "application/pdf"})));
						break;
					case ".mp3":
						setSrc(URL.createObjectURL(new Blob([blob], {type: "audio/mpeg"})));
						break;
					case ".wav":
						setSrc(URL.createObjectURL(new Blob([blob], {type: "audio/wav"})));
						break;
					case ".mp4":
						setSrc(URL.createObjectURL(new Blob([blob], {type: "video/mp4"})));
						break;
					case ".mov":
						setSrc(URL.createObjectURL(new Blob([blob], {type: "video/quicktime"})));
						break;
					case ".zip":
						const zipFileReader = new BlobReader(blob);
						const zipReader = new ZipReader(zipFileReader);
						const entries = await zipReader.getEntries();
						setEntries(entries);
						await zipReader.close();
						break;
					default:
						setSrc(URL.createObjectURL(blob));
						break;
				}
			}

		})()
	}, [blob, entry.filename]);

	const previewComponent = useMemo(() => {
		if (blob) {
			// extname keeps whatever casing the archive stored, so normalise before matching (DEV-3002)
			const extension = extname(entry.filename).toLowerCase();

			switch (extension) {
				//text files
				case ".eml":
				case ".rtf":
				case ".html":
				case ".txt":
				case ".json":
				case ".csv":
				case ".tsv":
					return <iframe className={""} width={'100%'} height={500} srcDoc={srcDoc}></iframe>;

				case ".pdf":
					return <iframe className={""} width={'100%'} height={500} src={src}></iframe>;

				//audio files
				case ".mp3":
				case ".wav":
					return <audio controls src={src} />;

				//video files
				case ".mp4":
				case ".mov":
					return <video controls style={{maxHeight: "100px", width: "auto", height: "auto"}} src={src} />

				//image files
				case ".heic":
				case ".heif":
				case ".png":
				case ".jpg":
				case ".jpeg":
				case ".avif":
				case ".gif":
				case ".svg":
					return <img className={"img-fluid"} src={src}/>;

				case ".zip":
					break;

				default:
					return <DownloadFile blob={blob} fileName={entry.filename} />;


			}
		}
		else {
			return null;
		}
	}, [src, srcDoc, entry.filename]);


	function hide() {
		setBlob(null);
	}

	let className = "";
	if (entry.directory || entry.filename.endsWith(".zip")) {
		className = "table-dark";
	}

	return (
		<>
			<tr className={className}>
				<td>
					{blob ?
						<>
							<a href={"#"} className={"d-block"} onClick={hide}>{parent && ' -- ' + parent + '/'} {entry.filename}</a>
							{previewComponent}
						</>
						:
						entry.directory ? (parent ? ' -- ' + parent + '/': '')+entry.filename: <a href={"#"} disabled={loading} onClick={onClick}>{loading && <Spinner small={true} />}{parent && ' -- ' + parent + '/'}{entry.filename}</a>
					}
				</td>
				<td>{!entry.directory && entry.uncompressedSize}</td>
			</tr>
			{entries.map(value => {
				return (
					<Entry key={value.filename} parent={entry.filename} entry={value}/>
				);
			})}
		</>

	);
}
